import React, {
  createContext,
  useContext,
  useEffect,
  // useLayoutEffect,
  // useMemo,
  useState,
} from "react";
import { useRouter } from "next/router";
// import { getInitialURL } from "@/utils/helpers";
import Cookies from "js-cookie";
import { io, Socket } from "socket.io-client";

import { infoToast } from "@/component/toasts";
import { getPreferredUsersAsync } from "@/redux/actions/preferredTerms/PreferredTerms";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { addAppMiddleware } from "@/redux/middleware";
import { createSocketMiddleware } from "@/redux/middleware/createSocketMiddleware";
import { setUser } from "@/redux/reducers/user";
import {
  resetFilters,
  setSelectedServiceUser,
} from "@/redux/reducers/service-users";
import { getCurrentStaffUser } from "@/services/staff";
import { ACCESS_LEVEL } from "@/utils/constants";
import logger from "@/runtime-utils/observability/logger";
import { getPublicEnv } from "@/runtime-utils/config/public-env";
import { useMessageUnreadCounts } from "./useMessageUnreadCounts";
import { isSupportMode } from "@/utils/support-access";

export const SocketContext = createContext<any | null>(null);

export const useSocket = () => {
  const { socket } = useContext(SocketContext);
  return socket;
};
export const useSocketActions = () => {
  const { disconnectSocket } = useContext(SocketContext);
  return { disconnectSocket };
};

interface SocketProviderProps {
  children: React.ReactNode;
}

const decodeJwtPayload = (token: string) => {
  try {
    const [, payload] = token.split(".");

    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const normalized = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    return JSON.parse(window.atob(normalized));
  } catch {
    return null;
  }
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const { user } = useAppSelector((state) => state.user);
  const tenant =
    Cookies.get("tenant") ||
    (typeof window !== "undefined"
      ? window.localStorage.getItem("tenant") || undefined
      : undefined);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isUnreadMsgs, familyUnreadCount } = useMessageUnreadCounts({
    userId: user._id,
    tenant,
    socket,
  });

  useEffect(() => {
    const token = Cookies.get("token");

    if (!token || user._id) {
      return;
    }

    const tokenPayload = decodeJwtPayload(token);

    if (isSupportMode()) {
      if (tokenPayload?.id) {
        dispatch(
          setUser({
            token,
            user: {
              _id: tokenPayload.id,
              name: "Support User",
              accessLevel: ACCESS_LEVEL.ADMINISTRATOR,
              approved: true,
              role: "support",
              isSupportUser: true,
            },
          })
        );
      }
      return;
    }

    if (!tenant) {
      return;
    }

    const hydrateUserFromSession = async () => {
      const response = await getCurrentStaffUser();

      if (response?.success && response?.data?._id) {
        dispatch(
          setUser({
            user: response.data,
            token,
          })
        );
        return;
      }

      if (tokenPayload?.id) {
        dispatch(
          setUser({
            token,
            user: {
              _id: tokenPayload.id,
              email: tokenPayload.email,
              name: tokenPayload.name,
              firstName: tokenPayload.firstName,
              lastName: tokenPayload.lastName,
              jobRoles: tokenPayload.jobRoles,
              accessLevel: tokenPayload.accessLevel,
              tenantId: tenant,
              approved: true,
            },
          })
        );
      }
    };

    void hydrateUserFromSession();
  }, [dispatch, tenant, user._id]);

  useEffect(() => {
    const preferredLookupId = tenant || user._id;

    if (preferredLookupId && (!isSupportMode() || tenant)) {
      dispatch(getPreferredUsersAsync(preferredLookupId));
    }
    dispatch(setSelectedServiceUser(null));
  }, [dispatch, tenant, user._id]);

  const mainPath = router.pathname?.split("/")?.[1];

  useEffect(() => {
    if (mainPath !== "service") {
      dispatch(resetFilters());
    }
  }, [dispatch, mainPath]);

  useEffect(() => {
    if (Object.keys(user).length > 0 && !socket) {
      const newSocket = io(getPublicEnv("NEXT_PUBLIC_BASE_URL"), {
        transports: ["websocket"],
        auth: { token: Cookies.get("token") },
        query: { userId: user?._id, tenant },
        withCredentials: true,
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSocket(newSocket);
      // Always emit join on every socket connect event
      newSocket.on("connect", () => {
        logger.debug("connected", newSocket);
        newSocket.emit("join", user?._id, user?.name);
        // Always request online status after connect
        newSocket.emit("get-online-status", (onlineList: any) => {
          logger.debug("online status after connect", onlineList);
        });

        // 🔌 Dynamically attach socket-aware middleware
        addAppMiddleware(createSocketMiddleware(newSocket));
      });
      newSocket.on("connect_error", (error) => {
        logger.error("Socket connection error:", error);
      });
    }
  }, [user, tenant, socket]);

  // Handle incoming messages and show toast if not on chat page
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (message: any) => {
      logger.debug("message", message, router.pathname);
      if (!router.pathname.includes("/chat") && message.senderId !== user._id) {
        infoToast("New message from " + message.senderName);
      }
    };

    socket.on("global-message", handleMessage);

    return () => {
      socket.off("global-message", handleMessage);
    };
  }, [socket, router.pathname, user]);

  // Provide a disconnectSocket function for consumers
  // Only call disconnectSocket on explicit logout or app close
  const disconnectSocket = () => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        isUnreadMsgs,
        familyUnreadCount,
        disconnectSocket,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
